import React from "react";
import { Link } from "react-router-dom";
import { MapPin, ChevronRight, PhoneCall } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import Breadcrumbs from "../components/Breadcrumbs";
import { Toaster } from "../components/ui/toaster";
import { ValuationProvider, useAppModals } from "../components/ValuationDialog";
import Seo from "../components/Seo";
import { AREA_GUIDES, CONTACT } from "../mock";

const CRUMBS = [{ label: "Home", to: "/" }, { label: "Areas we cover" }];

function Content() {
  const { openValuation } = useAppModals();

  return (
    <div className="App">
      <Seo
        title="Areas We Cover | Letting Agents in Hackney & East London | New Space"
        description="New Space are independent letting and property management agents covering London Fields, Dalston, Hackney Wick, Islington, Shoreditch, Stoke Newington and the City of London."
        path="/areas"
      />
      <Header />

      {/* Hero */}
      <section className="relative overflow-hidden bg-[#16323f] pt-32 pb-16 lg:pt-44">
        <div className="mx-auto max-w-[1000px] px-5 text-center">
          <div className="mb-6 flex justify-center">
            <Breadcrumbs variant="light" items={CRUMBS} />
          </div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#d8b074]">Local Expertise</p>
          <h1 className="mt-4 font-display text-4xl font-700 uppercase leading-tight tracking-wide text-white lg:text-6xl">
            Areas we cover
          </h1>
          <p className="mx-auto mt-5 max-w-2xl text-[15px] leading-relaxed text-white/85">
            Based in Haggerston, we specialise in lettings and management across East and North London —
            with London-wide reach through our partnership with HAUS Block Management.
          </p>
        </div>
      </section>

      {/* Areas grid */}
      <section className="bg-[#faf7f0] py-16 lg:py-24">
        <div className="mx-auto max-w-[1200px] px-5">
          <div className="grid gap-8 sm:grid-cols-2 lg:grid-cols-3">
            {AREA_GUIDES.map((a) => (
              <Link
                key={a.slug}
                to={`/areas/${a.slug}`}
                data-testid={`area-card-${a.slug}`}
                className="group flex flex-col overflow-hidden rounded-2xl border border-[#e5ddd0] bg-white shadow-sm transition-all duration-300 hover:-translate-y-1.5 hover:shadow-xl"
              >
                <div className="relative h-52 overflow-hidden">
                  <img
                    src={a.image}
                    onError={(e) => (e.currentTarget.src = a.fallback)}
                    alt={`Letting agents in ${a.name}`}
                    className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-[#16323f]/25" />
                  <span className="absolute left-4 top-4 flex items-center gap-1.5 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-[#16323f]">
                    <MapPin size={12} className="text-[#ec008c]" /> {a.postcode}
                  </span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h2 className="font-display text-2xl font-600 text-[#16323f]">{a.name}</h2>
                  <p className="mt-2 flex-1 text-sm leading-relaxed text-[#7a716a] line-clamp-3">
                    {a.paragraphs[0]}
                  </p>
                  <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.2em] text-[#ec008c]">
                    View area guide
                    <ChevronRight size={15} className="transition-transform group-hover:translate-x-1" />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#16323f] py-16">
        <div className="mx-auto flex max-w-[1000px] flex-col items-center px-5 text-center">
          <h2 className="font-display text-3xl font-600 uppercase tracking-wide text-white lg:text-4xl">
            Letting in your area?
          </h2>
          <p className="mt-3 text-white/75">Book a free, no-obligation valuation with our local team.</p>
          <div className="mt-8 flex flex-col gap-4 sm:flex-row">
            <button
              onClick={openValuation}
              data-testid="areas-valuation-btn"
              className="group flex items-center justify-center gap-2 rounded-full bg-[#ec008c] px-9 py-3.5 text-xs font-semibold uppercase tracking-[0.2em] text-white transition-colors hover:bg-[#c60074]"
            >
              Request a free valuation <ChevronRight size={16} className="transition-transform group-hover:translate-x-1" />
            </button>
          </div>
          <a href={`tel:${CONTACT.phone.replace(/\s/g, "")}`} className="mt-6 inline-flex items-center gap-2 text-sm text-white/80 hover:text-white">
            <PhoneCall size={16} className="text-[#d8b074]" /> Or call us on {CONTACT.phone}
          </a>
        </div>
      </section>

      <Footer />
      <Toaster />
    </div>
  );
}

export default function AreasIndex() {
  return (
    <ValuationProvider>
      <Content />
    </ValuationProvider>
  );
}
